/**
 * CSV bulk upload for admin social publishing.
 *
 * Columns (header row required, names are case-insensitive):
 *   text          The post (required)
 *   action        now | schedule | draft. Blank: schedule when scheduled_at is set, else draft
 *   scheduled_at  "YYYY-MM-DD HH:mm" in the admin's time zone, or ISO 8601 with an offset
 *   accounts      Where to post: platform names (linkedin, x, bluesky), handles or account
 *                 names, separated by commas, semicolons or |. Blank: every connected account
 *   short_text    Optional shorter version used for X and Bluesky
 *   image         Optional image or MP4 video: a file name (chosen alongside the CSV) or an https link
 *   image_alt     Optional alt text describing it
 */

import type { SocialAccount, SocialBulkPostInput, SocialPlatform } from "@/types";
import { postLength } from "@/lib/socialText";
import { mediaProblem } from "@/lib/socialMedia";

export const MAX_BULK_ROWS = 100;

export type BulkAction = "now" | "schedule" | "draft";

export interface BulkRow {
  /** Line number in the file (header is line 1) */
  line: number;
  text: string;
  shortText: string;
  action: BulkAction;
  scheduledAt: Date | null;
  accounts: SocialAccount[];
  /** The image cell as written: a file name or an https link (images and MP4 videos) */
  image: string;
  imageAlt: string;
  /** The chosen file matching `image`, when it isn't a link */
  imageFile: File | null;
  problems: string[];
}

/** Storage key for a row's image, once uploaded */
export type UploadedImage = { key: string; contentType: string };

export const CSV_TEMPLATE = [
  "text,action,scheduled_at,accounts,short_text,image,image_alt",
  '"Competitors change pricing pages more often than you think. We watch them every hour so you don\'t have to. #CompetitiveIntelligence",now,,,,,',
  '"Your competitor\'s careers page is their roadmap. Here is how to read it.",schedule,2026-10-01 09:00,"linkedin,bluesky","Careers pages leak roadmaps. How to read them:",careers.png,"A careers page with three job titles highlighted"',
  '"Draft idea: what changelog cadence tells you about a team",draft,,linkedin,,changelog-walkthrough.mp4,',
].join("\n");

export const isImageLink = (value: string) => /^https?:\/\//i.test(value);
/** "images/Careers.PNG" and "careers.png" name the same chosen file */
export const imageFileKey = (name: string) => (name.trim().split(/[\\/]/).pop() ?? "").toLowerCase();

// ─── CSV parsing (RFC 4180: quoted fields, "" escapes, newlines inside quotes) ─

export function parseCsv(input: string): string[][] {
  const text = input.replace(/^﻿/, "");
  // Excel in many locales saves with semicolons
  const firstLine = text.slice(0, text.search(/\r?\n|$/));
  const delimiter = !firstLine.includes(",") && firstLine.includes(";") ? ";" : ",";

  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"' && field === "") {
      quoted = true;
    } else if (char === delimiter) {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const COLUMN_ALIASES: Record<string, string[]> = {
  text: ["text", "body", "post", "content", "message"],
  action: ["action", "tag", "type", "mode", "when_to_post"],
  scheduledAt: ["scheduled_at", "schedule", "scheduled", "date", "datetime", "time", "publish_at"],
  accounts: ["accounts", "account", "platforms", "platform", "to", "channels"],
  shortText: ["short_text", "short", "x_text", "bluesky_text", "short_version"],
  image: ["image", "image_url", "image_file", "media", "media_url", "video", "video_url", "photo", "picture"],
  imageAlt: ["image_alt", "alt", "alt_text"],
};

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

const ACTION_ALIASES: Record<string, BulkAction> = {
  now: "now",
  "post now": "now",
  "publish now": "now",
  publish: "now",
  immediately: "now",
  schedule: "schedule",
  scheduled: "schedule",
  later: "schedule",
  draft: "draft",
  drafts: "draft",
};

const PLATFORM_ALIASES: Record<string, SocialPlatform> = {
  linkedin: "linkedin",
  li: "linkedin",
  x: "x",
  twitter: "x",
  bluesky: "bluesky",
  bsky: "bluesky",
};

/** "2026-10-01 09:00" is local time; anything with Z or an offset is taken as is. */
export function parseWhen(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const local = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (local) {
    const [, y, mo, d, h = "9", mi = "0", s = "0"] = local;
    const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
    // new Date rolls 2026-13-40 over into 2027; reject instead
    const exact =
      date.getMonth() === Number(mo) - 1 && date.getDate() === Number(d) && date.getHours() === Number(h) && date.getMinutes() === Number(mi);
    return exact ? date : null;
  }
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) {
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function matchAccounts(value: string, accounts: SocialAccount[]): { matched: SocialAccount[]; unknown: string[] } {
  const usable = accounts.filter((a) => a.status === "active");
  const tokens = value
    .split(/[,;|]/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length === 0) return { matched: usable, unknown: [] };

  const matched = new Map<string, SocialAccount>();
  const unknown: string[] = [];
  for (const token of tokens) {
    const key = token.toLowerCase().replace(/^@/, "");
    const platform = PLATFORM_ALIASES[key];
    const hits = platform
      ? usable.filter((a) => a.platform === platform)
      : usable.filter(
          (a) =>
            a.id === token ||
            a.username?.toLowerCase() === key ||
            a.displayName.toLowerCase() === key,
        );
    if (hits.length === 0) unknown.push(platform ? `${token} (no active ${token} account)` : token);
    for (const hit of hits) matched.set(hit.id, hit);
  }
  return { matched: [...matched.values()], unknown };
}

/** `images` are the files chosen alongside the CSV, matched to the image column by file name. */
export function buildRows(
  csv: string,
  accounts: SocialAccount[],
  images: File[] = [],
): { rows: BulkRow[]; error: string | null } {
  const table = parseCsv(csv).filter((row) => row.some((cell) => cell.trim() !== ""));
  if (table.length < 2) return { rows: [], error: "The file needs a header row and at least one post." };

  const header = table[0].map(normalizeHeader);
  const column = (name: keyof typeof COLUMN_ALIASES) =>
    header.findIndex((h) => COLUMN_ALIASES[name].includes(h));
  const index = {
    text: column("text"),
    action: column("action"),
    scheduledAt: column("scheduledAt"),
    accounts: column("accounts"),
    shortText: column("shortText"),
    image: column("image"),
    imageAlt: column("imageAlt"),
  };
  const filesByName = new Map(images.map((file) => [imageFileKey(file.name), file]));
  if (index.text === -1) return { rows: [], error: 'No "text" column found. Download the template to see the format.' };

  const body = table.slice(1);
  if (body.length > MAX_BULK_ROWS) {
    return { rows: [], error: `The file has ${body.length} posts; upload at most ${MAX_BULK_ROWS} at a time.` };
  }

  const cell = (row: string[], i: number) => (i >= 0 ? (row[i] ?? "").trim() : "");
  const rows = body.map((cells, i): BulkRow => {
    const problems: string[] = [];
    const text = cell(cells, index.text);
    const shortText = cell(cells, index.shortText);
    const whenRaw = cell(cells, index.scheduledAt);
    const scheduledAt = parseWhen(whenRaw);
    const actionRaw = cell(cells, index.action).toLowerCase();
    const image = cell(cells, index.image);
    const imageAlt = cell(cells, index.imageAlt);
    const imageFile = image && !isImageLink(image) ? (filesByName.get(imageFileKey(image)) ?? null) : null;

    let action: BulkAction;
    if (actionRaw) {
      const known = ACTION_ALIASES[actionRaw];
      if (!known) problems.push(`Unknown action "${actionRaw}" (use now, schedule or draft)`);
      action = known ?? "draft";
    } else {
      action = whenRaw ? "schedule" : "draft";
    }

    if (whenRaw && !scheduledAt) problems.push(`Can't read the date "${whenRaw}" (use YYYY-MM-DD HH:mm)`);
    if (action === "schedule" && !whenRaw) problems.push("Scheduled posts need a scheduled_at time");

    const { matched, unknown } = matchAccounts(cell(cells, index.accounts), accounts);
    if (unknown.length) problems.push(`No connected account matches: ${unknown.join(", ")}`);
    else if (matched.length === 0) problems.push("No connected account to post to");

    if (!text) problems.push("Text is empty");
    for (const platform of new Set(matched.map((a) => a.platform))) {
      const content = platform !== "linkedin" && shortText ? shortText : text;
      const { length, max } = postLength(platform, content);
      if (length > max) {
        const name = platform === "x" ? "X" : platform === "bluesky" ? "Bluesky" : "LinkedIn";
        problems.push(
          `Too long for ${name} (${length}/${max})${platform !== "linkedin" && !shortText ? "; add a short_text" : ""}`,
        );
      }
    }

    if (image) {
      if (isImageLink(image)) {
        if (!/^https:\/\//i.test(image)) problems.push("Image links must start with https://");
      } else if (!imageFile) {
        problems.push(`"${image}" not chosen yet (use Choose images or videos)`);
      } else {
        const problem = mediaProblem(imageFile, matched.map((a) => a.platform));
        if (problem) problems.push(`"${image}" ${problem}`);
      }
    }
    if (imageAlt.length > 1000) problems.push("Alt text is limited to 1000 characters");

    return { line: i + 2, text, shortText, action, scheduledAt, accounts: matched, image, imageAlt, imageFile, problems };
  });

  return { rows, error: null };
}

/**
 * Turn valid rows into API input. With staggerMinutes > 0, "now" rows are
 * spread out (first one now, the next N minutes later, ...) so a batch
 * doesn't land on followers all at once.
 */
export function toBulkInput(
  rows: BulkRow[],
  staggerMinutes: number,
  uploaded: Map<number, UploadedImage> = new Map(),
): SocialBulkPostInput[] {
  let nowIndex = 0;
  return rows.map((row) => {
    const targets = row.accounts.map((account) => ({
      accountId: account.id,
      bodyOverride: account.platform !== "linkedin" && row.shortText ? row.shortText : null,
    }));
    const image = uploaded.get(row.line);
    const base = {
      body: row.text,
      targets,
      imageKey: image?.key ?? null,
      imageType: image?.contentType ?? null,
      imageAlt: image ? row.imageAlt || null : null,
    };

    if (row.action === "draft") {
      return { ...base, status: "draft" as const, scheduledAt: row.scheduledAt?.toISOString() ?? null };
    }
    if (row.action === "schedule") {
      return { ...base, status: "scheduled" as const, scheduledAt: row.scheduledAt!.toISOString() };
    }
    const offset = staggerMinutes * nowIndex++;
    if (offset === 0) return { ...base, status: "scheduled" as const, publishNow: true };
    return {
      ...base,
      status: "scheduled" as const,
      scheduledAt: new Date(Date.now() + offset * 60_000).toISOString(),
    };
  });
}
